import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Settings, Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import RightDrawer from '@/shared/components/form/RightDrawer';
import PageBreadcrumb from '@/shared/PageBreadCrumb';
import RoleForm from '../components/RoleForm';
import DeptFeaturesDrawer from '../components/DeptFeaturesDrawer';
import UserDetailsPanel from '../components/UserDetailsPanel';
import PermissionEditorDrawer from '../components/PermissionEditorDrawer';
import { buildRoleColumns, RoleToggleStatusCell } from '../components/roleColumns';
import type { Role } from '@/types/role';
import RoleService from '@/services/roleService';
import { DataTableShell } from '@/components/data-table';
import type { ActiveFilterChip } from '@/components/data-table';
import { SOVEREIGN } from '@/features/epc/utils/epcProjectVisual';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ITEMS_PER_PAGE = 10;
const DEPARTMENTS = ['OMS', 'AMS', 'EPC'] as const;

// ---------------------------------------------------------------------------
// Sovereign Emerald department tab styling
// ---------------------------------------------------------------------------

const DEPT_STYLES: Record<'OMS' | 'AMS' | 'EPC', { activeColor: string; activeAccent: string }> = {
  OMS: { activeColor: SOVEREIGN.bottle, activeAccent: 'rgba(31,90,71,0.08)' },
  AMS: { activeColor: '#4B3B78', activeAccent: 'rgba(107,90,138,0.08)' },
  EPC: { activeColor: '#7A5A20', activeAccent: 'rgba(201,169,97,0.10)' },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Roles() {
  const navigate = useNavigate();

  // ── Fetch state ──────────────────────────────────────────────────────────
  const [roles, setRoles] = useState<Role[]>([]);
  const [totalRoles, setTotalRoles] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // ── Filter / pagination / sort state ────────────────────────────────────
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [status, setStatus] = useState<'active' | 'inactive' | null>(null);
  const [department, setDepartment] = useState<'OMS' | 'AMS' | 'EPC'>('OMS');
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(ITEMS_PER_PAGE);
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // ── Drawer / panel state ─────────────────────────────────────────────────
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [featureDrawerDept, setFeatureDrawerDept] = useState<'OMS' | 'AMS' | 'EPC' | null>(null);

  // Users panel
  const [userPanelRole, setUserPanelRole] = useState<Role | null>(null);

  // Permission editor drawer
  const [permissionRole, setPermissionRole] = useState<Role | null>(null);
  const [isPermissionDrawerOpen, setIsPermissionDrawerOpen] = useState<boolean>(false);

  // ── Debounce search ───────────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // ── Reset page on filter/dept change ─────────────────────────────────────
  const resetPage = () => setPage(1);

  useEffect(() => {
    resetPage();
  }, [debouncedSearch, status, department]);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchRoles = useCallback(async () => {
    try {
      setIsLoading(true);
      const params: {
        page: number;
        limit: number;
        name?: string;
        status?: string | null;
        includeUsers?: boolean;
        department?: string;
        sortBy?: string;
      } = {
        page,
        limit: pageSize,
        includeUsers: true,
        department,
        sortBy: `${sortField}:${sortOrder}`,
      };

      if (debouncedSearch.trim()) params.name = debouncedSearch.trim();
      if (status) params.status = status;

      const res = await RoleService.getRoles(params);

      if (res?.success && res?.data && Array.isArray(res.data)) {
        setRoles(res.data);
        setTotalRoles(res.meta?.totalDocs ?? res.data.length);
      } else {
        setRoles([]);
        setTotalRoles(0);
      }
    } catch (err) {
      console.error('Failed to fetch roles', err);
      toast.error('Failed to fetch roles');
      setRoles([]);
      setTotalRoles(0);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch, status, page, pageSize, department, sortField, sortOrder]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // ── Form handlers ─────────────────────────────────────────────────────────
  const handleFormClose = () => {
    setOpenForm(false);
    setEditingRole(null);
  };

  const handleSaved = () => {
    setEditingRole(null);
    setOpenForm(false);
    fetchRoles();
  };

  // ── Active filter chips ───────────────────────────────────────────────────
  const activeFilters = useMemo<ActiveFilterChip[]>(() => {
    const chips: ActiveFilterChip[] = [];
    if (status) chips.push({ key: 'status', value: status, display: `Status: ${status}` });
    return chips;
  }, [status]);

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = useMemo(
    () =>
      buildRoleColumns({
        onEdit: (role) => {
          setEditingRole(role);
          setOpenForm(true);
        },
        onViewUsers: (role) => setUserPanelRole(role),
        onPermissions: (role) => {
          setPermissionRole(role);
          setIsPermissionDrawerOpen(true);
        },
        onMenuOrder: (role) => {
          navigate(`/admin/roles/${role.id ?? role._id}/menu`, { state: { role } });
        },
        toggleCell: (role) => {
          const roleId = role.id ?? role._id ?? '';
          const isDefaultRole = !!(role as Role & { isDefault?: boolean }).isDefault;
          const isDisabled = isDefaultRole && role.isActive;
          return (
            <RoleToggleStatusCell
              role={role}
              disabled={isDisabled}
              onConfirm={async () => {
                if (role.isActive) {
                  await RoleService.deactivateRole(roleId);
                } else {
                  await RoleService.activateRole(roleId);
                }
                fetchRoles();
              }}
            />
          );
        },
      }),
    // fetchRoles is stable per useCallback deps — eslint-disable acceptable here
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchRoles, navigate],
  );

  // ── Department tab style helpers ──────────────────────────────────────────
  const tabStyle = (dept: 'OMS' | 'AMS' | 'EPC') => {
    const active = department === dept;
    const ds = DEPT_STYLES[dept];
    return {
      color: active ? ds.activeColor : SOVEREIGN.kpiCaption,
      background: active ? ds.activeAccent : 'transparent',
      border: active ? `1px solid ${ds.activeColor}26` : '1px solid transparent',
    };
  };

  const dotStyle = (dept: 'OMS' | 'AMS' | 'EPC') => ({
    background: department === dept ? DEPT_STYLES[dept].activeColor : SOVEREIGN.kpiCaption,
    opacity: department === dept ? 1 : 0.4,
  });

  const settingsStyle = (dept: 'OMS' | 'AMS' | 'EPC') => ({
    color: department === dept ? DEPT_STYLES[dept].activeColor : SOVEREIGN.kpiCaption,
  });

  return (
    <div className="mx-auto max-w-9xl space-y-6">
      <PageBreadcrumb
        pageTitle="Role Management"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Role Management' },
        ]}
      />

      {/* ── Department Tabs (Sovereign Emerald) ──────────────────────────── */}
      <div className="flex items-center gap-1 flex-wrap">
        <div
          className="flex gap-0.5 p-1 rounded-xl"
          style={{
            background: SOVEREIGN.inputBg,
            border: `1px solid ${SOVEREIGN.cardBorder}`,
          }}
        >
          {DEPARTMENTS.map((dept) => (
            <div key={dept} className="flex items-center">
              <button
                type="button"
                onClick={() => setDepartment(dept)}
                className="flex items-center gap-2 px-4 py-1.5 text-sm font-semibold rounded-lg transition-all duration-200"
                style={tabStyle(dept)}
              >
                <span
                  className="w-2 h-2 rounded-full shrink-0 transition-colors"
                  style={dotStyle(dept)}
                />
                {dept}
              </button>
              {/* Settings gear */}
              <button
                type="button"
                title={`Manage ${dept} features`}
                onClick={() => setFeatureDrawerDept(dept)}
                className="ml-0.5 mr-1 p-1.5 rounded-lg transition-all duration-150"
                style={settingsStyle(dept)}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = DEPT_STYLES[dept].activeAccent;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                <Settings size={13} aria-hidden />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ── DataTableShell ────────────────────────────────────────────────── */}
      <DataTableShell<Role>
        title="Role Management"
        subtitle={`${totalRoles} role${totalRoles !== 1 ? 's' : ''} · ${department}`}
        icon={<ShieldCheck size={20} />}
        primaryAction={{
          label: 'Add Role',
          icon: <Plus size={16} />,
          onClick: () => {
            setEditingRole(null);
            setOpenForm(true);
          },
        }}
        columns={columns}
        data={roles}
        rowKey={(r) => r.id ?? r._id ?? ''}
        loading={isLoading}
        emptyState={{
          title: 'No roles found',
          description:
            activeFilters.length || search
              ? 'Try adjusting your search or filters.'
              : `Get started by adding your first ${department} role.`,
        }}
        toolbar={{
          search: {
            value: search,
            onChange: (v) => {
              setSearch(v);
              resetPage();
            },
            placeholder: 'Search roles…',
          },
          filters: [
            {
              key: 'status',
              label: 'Status',
              options: [
                { label: 'Active', value: 'active' },
                { label: 'Inactive', value: 'inactive' },
              ],
              value: status,
              onChange: (v) => {
                setStatus((v as 'active' | 'inactive' | null) || null);
                resetPage();
              },
            },
          ],
          activeFilters,
          onClearFilter: (chip) => {
            if (chip.key === 'status') setStatus(null);
            resetPage();
          },
          onClearAll: () => {
            setStatus(null);
            resetPage();
          },
        }}
        pagination={{
          page,
          pageSize,
          total: totalRoles,
          onChange: (p, ps) => {
            setPage(p);
            // pageSize is fixed; if shell sends different value, it's ignored
            void ps;
          },
        }}
        sortField={sortField}
        sortOrder={sortOrder}
        onSortChange={(field, order) => {
          setSortField(field ?? 'createdAt');
          setSortOrder(order ?? 'desc');
          resetPage();
        }}
      />

      {/* ── Role Form Drawer ──────────────────────────────────────────────── */}
      <RightDrawer
        open={openForm}
        title=""
        size="full"
        onClose={handleFormClose}
      >
        <RoleForm
          editingRole={editingRole}
          onAdd={handleSaved}
          onClose={handleFormClose}
          department={department}
        />
      </RightDrawer>

      {/* ── Department Features Drawer ────────────────────────────────────── */}
      {featureDrawerDept && (
        <DeptFeaturesDrawer
          department={featureDrawerDept}
          isOpen={!!featureDrawerDept}
          onClose={() => setFeatureDrawerDept(null)}
        />
      )}

      {/* ── User Details Panel ────────────────────────────────────────────── */}
      {userPanelRole && (
        <UserDetailsPanel
          isOpen={!!userPanelRole}
          onClose={() => setUserPanelRole(null)}
          roleName={userPanelRole.name}
          userCount={userPanelRole.userCount ?? 0}
          userDetails={userPanelRole.userDetails ?? []}
        />
      )}

      {/* ── Permission Editor Drawer ──────────────────────────────────────── */}
      <PermissionEditorDrawer
        open={isPermissionDrawerOpen}
        onClose={() => {
          setIsPermissionDrawerOpen(false);
          setPermissionRole(null);
        }}
        role={permissionRole}
        onSaved={fetchRoles}
      />

    </div>
  );
}
